import React, { useState, useEffect, useCallback } from "react";
import { X, AlertCircle, Copy, Check, Code2 } from "lucide-react";
import { toast } from "react-toastify";
import WhatsAppNewPreview from "./WhatsAppNewPreview";
import { extractVariablesFromTemplateJson } from "../utils/templateUtils";
import { DEFAULT_TEMPLATE_LANGUAGE, getLanguageLabel } from "../constants/templateLanguages";
import { getCategoryLabel } from "../constants/templateFilterOptions";

const ViewEditTemplateModal = ({ isOpen, onClose, template }) => {
  const [previewData, setPreviewData] = useState(null);
  const [jsonCopied, setJsonCopied] = useState(false);

  const buildApiSendPayload = useCallback(() => {
    if (!template) return null;
    const templateJson = template.template_json || {};
    const comps = templateJson.components || [];
    const paramFormat = template.parameter_format || templateJson.parameter_format || "numbered";
    const isNamed = paramFormat === "named";
    const isAuth = (template.category || "").toUpperCase() === "AUTHENTICATION";

    const headerNamedNames = [];
    const bodyNamedNames = [];
    if (isNamed) {
      const hdr = comps.find(c => c.type === "HEADER");
      (hdr?.example?.header_text_named_params || []).forEach(p => headerNamedNames.push(p.param_name));
      const bdy = comps.find(c => c.type === "BODY");
      (bdy?.example?.body_text_named_params || []).forEach(p => bodyNamedNames.push(p.param_name));
    }

    const apiComponents = [];
    const BUTTON_SUB_TYPE_MAP = { QUICK_REPLY: "quick_reply", URL: "url", PHONE_NUMBER: "voice_call", COPY_CODE: "copy_code", OTP: "url" };

    // --- HEADER ---
    const headerComp = comps.find(c => c.type === "HEADER");
    if (headerComp) {
      if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
        const mediaType = headerComp.format.toLowerCase();
        apiComponents.push({
          type: "header",
          parameters: [{ type: mediaType, [mediaType]: { link: "MEDIA_URL_HERE" } }],
        });
      } else if (headerComp.format === "TEXT" && headerComp.text) {
        const namedParams = headerComp.example?.header_text_named_params;
        const numberedSamples = headerComp.example?.header_text;
        if (namedParams && Array.isArray(namedParams) && namedParams.length > 0) {
          apiComponents.push({
            type: "header",
            parameters: namedParams.map((p, i) => {
              const param = { type: "text", text: p.example || "HEADER_VAR" };
              if (isNamed && headerNamedNames[i]) param.parameter_name = headerNamedNames[i];
              return param;
            }),
          });
        } else if (numberedSamples && numberedSamples.length > 0) {
          apiComponents.push({
            type: "header",
            parameters: numberedSamples.map(s => ({ type: "text", text: s })),
          });
        } else {
          const vars = headerComp.text.match(/\{\{[^}]+\}\}/g) || [];
          if (vars.length > 0) {
            apiComponents.push({
              type: "header",
              parameters: vars.map(() => ({ type: "text", text: "HEADER_VAR" })),
            });
          }
        }
      }
    }

    // --- BODY ---
    const bodyComp = comps.find(c => c.type === "BODY");
    if (bodyComp) {
      if (isAuth && bodyComp.add_security_recommendation !== undefined) {
        apiComponents.push({
          type: "body",
          parameters: [{ type: "text", text: "YOUR_OTP_CODE" }],
        });
      } else {
        const bodyVars = extractVariablesFromTemplateJson(templateJson).filter(v => v.target === "body");
        if (bodyVars.length > 0) {
          const namedParams = bodyComp.example?.body_text_named_params;
          if (namedParams && Array.isArray(namedParams) && namedParams.length > 0) {
            apiComponents.push({
              type: "body",
              parameters: namedParams.map((p, i) => {
                const param = { type: "text", text: p.example || bodyVars[i]?.sample || "BODY_VAR" };
                if (isNamed && bodyNamedNames[i]) param.parameter_name = bodyNamedNames[i];
                return param;
              }),
            });
          } else {
            apiComponents.push({
              type: "body",
              parameters: bodyVars.map((v, i) => ({ type: "text", text: v.sample || `BODY_VAR_${i + 1}` })),
            });
          }
        }
      }
    }

    // --- BUTTONS ---
    const buttonsComp = comps.find(c => c.type === "BUTTONS");
    console.log("buttonsComp", buttonsComp);
    if (buttonsComp?.buttons) {
      buttonsComp.buttons.forEach((btn, btnIdx) => {
        const parameters = [];

        if (btn.type === "QUICK_REPLY") {
          parameters.push({ type: "payload", payload: btn.text || "REPLY" });
        } else if (btn.type === "URL") {
          if (!btn.url || !btn.url.includes("{{")) return;
          parameters.push({ type: "text", text: btn.example?.[0] || "URL_DYNAMIC_SUFFIX" });
        } else if (btn.type === "COPY_CODE" || btn.type === "COPY_OFFER_CODE") {
          const code = btn.example?.[0] || btn.example || "YOUR_CODE";
          parameters.push({ type: "coupon_code", coupon_code: typeof code === "string" ? code : "YOUR_CODE" });
        } else if (btn.type === "OTP") {
          parameters.push({ type: "coupon_code", coupon_code: "YOUR_OTP_CODE" });
        } else if (btn.type === "PHONE_NUMBER") {
          return;
        }

        apiComponents.push({
          type: "button",
          sub_type: BUTTON_SUB_TYPE_MAP[btn.type] || btn.type.toLowerCase(),
          index: String(btnIdx),
          parameters,
        });
      });
    }

    return {
      phone_number: "YOUR_PHONE_NUMBER",
      to: "RECIPIENT_PHONE_NUMBER",
      template_name: template.name,
      language: template.language || DEFAULT_TEMPLATE_LANGUAGE,
      components: apiComponents,
    };
  }, [template]);

  const handleCopyCurl = useCallback(() => {
    const payload = buildApiSendPayload();
    if (!payload) return;
    const jsonBody = JSON.stringify(payload, null, 2);
    const curlCmd = `curl -X POST https://rest.blutec.ai/api/v1/messages/template \\\n  -H "X-API-Key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '${jsonBody}'`;
    navigator.clipboard.writeText(curlCmd);
    setJsonCopied(true);
    toast.success("cURL command copied!");
    setTimeout(() => setJsonCopied(false), 2500);
  }, [buildApiSendPayload]);

  const handleCopyJson = useCallback(() => {
    const payload = buildApiSendPayload();
    if (!payload) return;
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setJsonCopied(true);
    toast.success("JSON payload copied!");
    setTimeout(() => setJsonCopied(false), 2500);
  }, [buildApiSendPayload]);

  useEffect(() => {
    if (template) {
      // Transform template data to match WhatsAppNewPreview expected format
      const templateJson = template.template_json || {};
      const components = templateJson.components || [];
      
      // Extract body from BODY component
      const bodyComponent = components.find(c => c.type === 'BODY');
      const body = bodyComponent?.text || template.body || '';
      
      // Extract footer from FOOTER component
      const footerComponent = components.find(c => c.type === 'FOOTER');
      const footer = footerComponent?.text || template.footer || '';
      
      // Extract header from HEADER component
      const headerComponent = components.find(c => c.type === 'HEADER');
      const headerFormat = headerComponent?.format || template.header_type || template.headerFormat || 'NONE';
      const headerText = headerComponent?.text || template.headerText || '';
      const headerExample = headerComponent?.example || {};
      const headerMediaUrl =  template.header_media_url || null;

      // Extract buttons from BUTTONS component
      const buttonsComponent = components.find(c => c.type === 'BUTTONS');
      const buttons = buttonsComponent?.buttons || template.buttons || [];
      
      // Extract variables properly from template_json using utility function
      const extractedVariables = extractVariablesFromTemplateJson(templateJson);
      
      // If no variables extracted, try to get from template.variables
      const variables = extractedVariables.length > 0 
        ? extractedVariables.map((v, index) => ({
            id: v.id || index + 1,
            name: v.name,
            sample: v.sample || `Sample ${v.name}`,
            target: v.target || "body"
          }))
        : (template.variables || []).map((v, index) => ({
            id: v.id || index + 1,
            name: v.name || String(v.id || index + 1),
            sample: v.sample || `Sample ${v.name || index + 1}`,
            target: v.target || "body"
          }));
      
      // Determine template type
      let templateType = 'text';
      if (buttons.length > 0) {
        templateType = 'buttons';
      } else if (headerFormat && headerFormat !== 'NONE' && headerFormat !== 'TEXT') {
        templateType = headerFormat.toLowerCase();
      }
      
      // Ensure headerFormat is always a string
      const finalHeaderFormat = String(headerFormat || 'NONE');
      const finalHeaderType = finalHeaderFormat.toLowerCase();
      
      setPreviewData({
        name: template.name || '',
        category: template.category || 'marketing',
        language: template.language || DEFAULT_TEMPLATE_LANGUAGE,
        templateType: template.template_type || templateType,
        headerFormat: finalHeaderFormat,
        headerType: finalHeaderType,
        headerText: headerText,
        header_media_url: headerMediaUrl,
        body: body,
        footer: footer,
        buttons: buttons.map((btn, idx) => ({
          id: idx + 1,
          type: btn.type,
          text: btn.text,
          url: btn.url,
          phone: btn.phone_number
        })),
        variables: variables
      });
    }
  }, [template, isOpen]);

  if (!isOpen || !template) return null;

  const templateJson = template.template_json || {};
  const components = templateJson.components || [];
  const bodyComponent = components.find(c => c.type === 'BODY');
  const footerComponent = components.find(c => c.type === 'FOOTER');
  const headerComponent = components.find(c => c.type === 'HEADER');
  const buttonsComponent = components.find(c => c.type === 'BUTTONS');
  const paramFormat = template.parameter_format || templateJson.parameter_format || "numbered";
  const statusKey = String(template.status || "").toLowerCase();
  const statusTone = statusKey === "approved"
    ? "g"
    : ["pending", "in_appeal", "paused", "flagged"].includes(statusKey)
      ? "a"
      : ["rejected", "disabled", "pending_deletion", "limit_exceeded"].includes(statusKey)
        ? "r"
        : "n";

  return (
    <>
      <div className="ping-drawer-backdrop" onClick={onClose} />
      <aside className="ping-drawer tpl-preview-drawer" role="dialog" aria-modal="true">
        <div className="ping-drawer-head">
          <div>
            <h3>{template.name}</h3>
            <p>
              {getCategoryLabel(template.category) || template.category || "Template"}
              {" · "}
              {getLanguageLabel(template.language) || template.language || "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ping-btn ping-btn-ghost ping-btn-sm"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="ping-drawer-body tpl-preview-body">
          <div className="tpl-pv-chips">
            <span className={`ping-chip ${statusTone}`}>{statusKey || "draft"}</span>
            <span className={`ping-chip ${paramFormat === "named" ? "b" : "g"}`}>
              {paramFormat === "named" ? "Named" : "Numbered"}
            </span>
            {headerComponent?.format && headerComponent.format !== "NONE" && (
              <span className="ping-chip n">{headerComponent.format}</span>
            )}
          </div>

          {statusKey === "rejected" && template.rejected_reason && (
            <div className="ping-banner red">
              <AlertCircle size={16} />
              <div>
                <b>Rejected</b>
                <p>{template.rejected_reason}</p>
              </div>
            </div>
          )}

          {previewData && (
            <div className="tpl-pv-wa">
              <WhatsAppNewPreview templateData={previewData} />
            </div>
          )}

          {(headerComponent || bodyComponent || footerComponent?.text || buttonsComponent?.buttons?.length) && (
            <div className="tpl-pv-card">
              {headerComponent && (
                <div className="tpl-pv-block">
                  <span>Header{headerComponent.format ? ` · ${headerComponent.format}` : ""}</span>
                  <p>{headerComponent.text || "Media header"}</p>
                </div>
              )}
              {bodyComponent && (
                <div className="tpl-pv-block">
                  <span>Body</span>
                  <p className="pre">{bodyComponent.text}</p>
                </div>
              )}
              {footerComponent?.text && (
                <div className="tpl-pv-block">
                  <span>Footer</span>
                  <p>{footerComponent.text}</p>
                </div>
              )}
              {buttonsComponent?.buttons?.length > 0 && (
                <div className="tpl-pv-block">
                  <span>Buttons</span>
                  <ul className="tpl-pv-btns">
                    {buttonsComponent.buttons.map((button, index) => (
                      <li key={index}>
                        <b>{button.text}</b>
                        <em>{String(button.type || "").replace(/_/g, " ")}</em>
                        {button.type === "URL" && button.url && <code>{button.url}</code>}
                        {button.type === "PHONE_NUMBER" && button.phone_number && (
                          <code>{button.phone_number}</code>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {previewData?.variables?.length > 0 && (
            <div className="tpl-pv-card">
              <div className="tpl-pv-block">
                <span>Variables</span>
                <div className="tpl-pv-vars">
                  {previewData.variables.map((variable) => (
                    <span key={variable.id} className="ping-chip n">
                      {`{{${variable.name}}}`}
                      {variable.sample ? ` · ${variable.sample}` : ""}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="ping-drawer-foot tpl-preview-foot">
          <div className="tpl-preview-copies">
            <button
              type="button"
              onClick={handleCopyCurl}
              className="ping-btn ping-btn-primary ping-btn-sm"
            >
              {jsonCopied ? <Check size={13} /> : <Code2 size={13} />}
              {jsonCopied ? "Copied!" : "Copy cURL"}
            </button>
            <button
              type="button"
              onClick={handleCopyJson}
              className="ping-btn ping-btn-ghost ping-btn-sm"
            >
              <Copy size={13} />
              Copy JSON
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ping-btn ping-btn-ghost ping-btn-sm"
          >
            Close
          </button>
        </div>
      </aside>
    </>
  );
};

export default ViewEditTemplateModal;
